import React, { useState, useRef, useMemo } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import {
  MeetingRequestFormData,
  MeetingRequestPayload,
  MeetingRequestResponse,
  MeetingFormErrors,
  LegislatorRole,
  MeetingType,
  LEGISLATOR_ROLE_LABELS,
  MEETING_TYPE_LABELS,
  INITIAL_FORM_DATA,
  FormState,
  BILL_OPTIONS,
} from '../types/legislatorMeeting';
import { US_STATES } from '../utils/usStates';
import { useBillPages } from '../hooks/useBillData';
import { trackLegislatorMeetingSubmit, trackSocialShare } from '../components/GoogleAnalytics';

interface LegislatorMeetingFormProps {
  id?: string;
}

const LegislatorMeetingForm: React.FC<LegislatorMeetingFormProps> = ({ id }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  // Form state
  const [formData, setFormData] = useState<MeetingRequestFormData>(INITIAL_FORM_DATA);
  const [formState, setFormState] = useState<FormState>('IDLE');
  const [errors, setErrors] = useState<MeetingFormErrors>({});
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  // Available bill options for the "Bill(s) to Be Discussed" field:
  // BILL_OPTIONS from the static bill list, plus any bills loaded live from
  // the Google Sheets "Bill Pages" tab, deduplicated and alphabetized.
  const { data: billPages } = useBillPages();
  const billOptions = useMemo(() => {
    const titles = new Set<string>(BILL_OPTIONS);
    billPages?.forEach((bill) => {
      if (bill.pageTitle) {
        titles.add(bill.pageTitle);
      }
    });
    return Array.from(titles).sort((a, b) => a.localeCompare(b));
  }, [billPages]);

  const handleShareLink = () => {
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      trackSocialShare('Copy Link', 'Legislator Meeting Form');
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // Handle text input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof MeetingFormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (formState === 'IDLE') setFormState('FILLING');
  };

  // Handle select changes
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user selects
    if (errors[name as keyof MeetingFormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if (formState === 'IDLE') setFormState('FILLING');
  };

  // Handle reCAPTCHA
  const handleCaptchaChange = (token: string | null) => {
    setCaptchaToken(token);
    if (errors.recaptcha) {
      setErrors((prev) => ({ ...prev, recaptcha: undefined }));
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaToken(null);
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: MeetingFormErrors = {};
    let isValid = true;

    // Required fields
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    }

    if (!formData.userPhoneNumber.trim()) {
      newErrors.userPhoneNumber = 'Phone number is required';
      isValid = false;
    } else {
      // eslint-disable-next-line no-useless-escape
      const phoneRegex = /^[\d\s\-\+\(\)]{7,20}$/;
      if (!phoneRegex.test(formData.userPhoneNumber)) {
        newErrors.userPhoneNumber = 'Please enter a valid phone number';
        isValid = false;
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email address';
        isValid = false;
      }
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
      isValid = false;
    }

    if (!formData.legislatorName.trim()) {
      newErrors.legislatorName = 'Legislator name is required';
      isValid = false;
    }

    if (!formData.legislatorRole) {
      newErrors.legislatorRole = 'Legislator role is required';
      isValid = false;
    }

    if (!formData.meetingType) {
      newErrors.meetingType = 'Meeting type is required';
      isValid = false;
    }

    if (!formData.proposedDateTime) {
      newErrors.proposedDateTime = 'Proposed date and time is required';
      isValid = false;
    }

    // Conditional validation based on meeting type
    if (formData.meetingType === 'VIDEO_CALL') {
      if (!formData.meetingLink.trim()) {
        newErrors.meetingLink = 'Meeting link is required for video calls';
        isValid = false;
      } else {
        try {
          const url = new URL(formData.meetingLink);
          if (!['http:', 'https:'].includes(url.protocol)) {
            newErrors.meetingLink = 'Please enter a valid URL starting with http:// or https://';
            isValid = false;
          }
        } catch {
          newErrors.meetingLink = 'Please enter a valid URL';
          isValid = false;
        }
      }
    }

    if (formData.meetingType === 'PHONE') {
      if (!formData.phoneNumber.trim()) {
        newErrors.phoneNumber = 'Phone number is required for phone calls';
        isValid = false;
      }
    }

    // reCAPTCHA validation
    if (!captchaToken) {
      newErrors.recaptcha = 'Please complete the CAPTCHA verification';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('VALIDATING');
    setErrorMessage('');

    if (!validateForm()) {
      setFormState('ERROR');
      requestAnimationFrame(() => {
        formRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      });
      return;
    }

    setFormState('SUBMITTING');

    try {
      const payload: MeetingRequestPayload = {
        ...formData,
        recaptchaToken: captchaToken!,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
      };

      const response = await fetch('/.netlify/functions/send-legislator-meeting-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const result: MeetingRequestResponse = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to submit meeting request');
      }

      // Success!
      setFormState('SUCCESS');
      trackLegislatorMeetingSubmit(true);
      setSuccessDialogOpen(true);

      // Reset form
      setFormData(INITIAL_FORM_DATA);
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    } catch (error) {
      console.error('Form submission error:', error);
      setFormState('ERROR');
      trackLegislatorMeetingSubmit(false);
      setErrorMessage(
        error instanceof Error ? error.message : 'Unable to submit your request. Please try again later.',
      );
    }
  };

  const handleCloseSuccessDialog = () => {
    setSuccessDialogOpen(false);
    setFormState('IDLE');
  };

  const isSubmitting = formState === 'SUBMITTING';

  return (
    <Box
      ref={formRef}
      id={id}
      component="form"
      tabIndex={-1}
      aria-labelledby="meeting-form-title"
      aria-describedby="meeting-form-description"
      aria-busy={isSubmitting}
      onSubmit={handleSubmit}
      noValidate
      sx={{
        scrollMarginTop: 112,
        '& .MuiInputBase-root': { minHeight: 48 },
        '& .MuiFormLabel-asterisk': { color: 'text.secondary' },
      }}
    >
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}
      >
        <Typography
          id="meeting-form-title"
          variant="h4"
          component="h2"
          sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '1.75rem' } }}
        >
          Request Robert’s participation
        </Typography>
        <Tooltip
          title="Copied!"
          placement="top"
          open={linkCopied}
          disableFocusListener
          disableHoverListener
          disableTouchListener
        >
          <Button
            onClick={handleShareLink}
            size="small"
            startIcon={<LinkIcon fontSize="small" />}
            variant="outlined"
            aria-label="Share link to this section"
            sx={{ textTransform: 'none' }}
          >
            Share
          </Button>
        </Tooltip>
      </Box>
      <Typography id="meeting-form-description" variant="body2" color="text.secondary" sx={{ mb: 4, lineHeight: 1.75 }}>
        Fill out the form below to request Robert Garza's participation in your legislator meeting. All fields marked
        with * are required.
      </Typography>

      {Object.values(errors).some(Boolean) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Please check the highlighted fields and complete the CAPTCHA before sending your request.
        </Alert>
      )}
      {/* Error Alert */}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box component="fieldset" sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
            <Typography component="legend" variant="h6" sx={{ mb: 3, fontWeight: 700, fontSize: '1.1rem' }}>
              1. Your Information
            </Typography>
            <Grid container spacing={3}>
              {/* Full Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="fullName"
                  name="fullName"
                  autoComplete="name"
                  label="Your Full Name"
                  value={formData.fullName}
                  onChange={handleChange}
                  error={!!errors.fullName}
                  helperText={errors.fullName}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 100, 'aria-required': 'true' }}
                />
              </Grid>

              {/* Phone Number */}
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="userPhoneNumber"
                  name="userPhoneNumber"
                  autoComplete="tel"
                  label="Your Phone Number"
                  type="tel"
                  value={formData.userPhoneNumber}
                  onChange={handleChange}
                  error={!!errors.userPhoneNumber}
                  helperText={errors.userPhoneNumber}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 20, 'aria-required': 'true' }}
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="email"
                  name="email"
                  autoComplete="email"
                  label="Your Email Address"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  disabled={isSubmitting}
                  inputProps={{ 'aria-required': 'true' }}
                />
              </Grid>

              {/* State */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!!errors.state}>
                  <InputLabel id="state-label">Your State</InputLabel>
                  <Select
                    labelId="state-label"
                    id="state"
                    aria-describedby={errors.state ? 'state-error' : undefined}
                    name="state"
                    value={formData.state}
                    label="Your State"
                    onChange={handleSelectChange}
                    disabled={isSubmitting}
                    inputProps={{ 'aria-required': 'true' }}
                  >
                    {US_STATES.map((state) => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.state && <FormHelperText id="state-error">{errors.state}</FormHelperText>}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box component="fieldset" sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
            <Typography component="legend" variant="h6" sx={{ mb: 3, fontWeight: 700, fontSize: '1.1rem' }}>
              2. Legislator Information
            </Typography>
            <Grid container spacing={3}>
              {/* Legislator Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  id="legislatorName"
                  name="legislatorName"
                  label="Legislator Name"
                  value={formData.legislatorName}
                  onChange={handleChange}
                  error={!!errors.legislatorName}
                  helperText={errors.legislatorName}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 100, 'aria-required': 'true' }}
                />
              </Grid>

              {/* Legislator Role */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required error={!!errors.legislatorRole}>
                  <InputLabel id="legislatorRole-label">Legislator Role</InputLabel>
                  <Select
                    labelId="legislatorRole-label"
                    id="legislatorRole"
                    aria-describedby={errors.legislatorRole ? 'legislatorRole-error' : undefined}
                    name="legislatorRole"
                    value={formData.legislatorRole}
                    label="Legislator Role"
                    onChange={handleSelectChange}
                    disabled={isSubmitting}
                    inputProps={{ 'aria-required': 'true' }}
                  >
                    {(Object.keys(LEGISLATOR_ROLE_LABELS) as LegislatorRole[]).map((role) => (
                      <MenuItem key={role} value={role}>
                        {LEGISLATOR_ROLE_LABELS[role]}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.legislatorRole && (
                    <FormHelperText id="legislatorRole-error">{errors.legislatorRole}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box component="fieldset" sx={{ m: 0, p: 0, border: 0, minWidth: 0 }}>
            <Typography component="legend" variant="h6" sx={{ mb: 3, fontWeight: 700, fontSize: '1.1rem' }}>
              3. Meeting Details
            </Typography>
            <Grid container spacing={3}>
              {/* Meeting Type */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth required error={!!errors.meetingType}>
                  <InputLabel id="meetingType-label">Meeting Type</InputLabel>
                  <Select
                    labelId="meetingType-label"
                    id="meetingType"
                    aria-describedby={errors.meetingType ? 'meetingType-error' : undefined}
                    name="meetingType"
                    value={formData.meetingType}
                    label="Meeting Type"
                    onChange={handleSelectChange}
                    disabled={isSubmitting}
                    inputProps={{ 'aria-required': 'true' }}
                  >
                    {(Object.keys(MEETING_TYPE_LABELS) as MeetingType[]).map((type) => (
                      <MenuItem key={type} value={type}>
                        {MEETING_TYPE_LABELS[type]}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.meetingType && <FormHelperText id="meetingType-error">{errors.meetingType}</FormHelperText>}
                </FormControl>
              </Grid>

              {/* Proposed Date & Time */}
              <Grid item xs={12} md={8}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label="Proposed Date & Time"
                    value={formData.proposedDateTime ? dayjs(formData.proposedDateTime) : null}
                    onChange={(newValue: Dayjs | null) => {
                      // Use format() instead of toISOString() to preserve the user's local time
                      // toISOString() converts to UTC, causing the email to show wrong time
                      const dateTimeStr = newValue ? newValue.format('YYYY-MM-DDTHH:mm:ss') : '';
                      setFormData((prev) => ({ ...prev, proposedDateTime: dateTimeStr }));
                      if (errors.proposedDateTime) {
                        setErrors((prev) => ({ ...prev, proposedDateTime: undefined }));
                      }
                      if (formState === 'IDLE') setFormState('FILLING');
                    }}
                    disabled={isSubmitting}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!errors.proposedDateTime,
                        helperText:
                          errors.proposedDateTime ||
                          'Use your local time. Our team will coordinate the time zone with you.',
                        inputProps: { 'aria-required': 'true' },
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* Conditional fields based on meeting type */}

              {/* Video Call fields */}
              {formData.meetingType === 'VIDEO_CALL' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      id="meetingLink"
                      name="meetingLink"
                      label="Meeting Link"
                      placeholder="https://zoom.us/j/..."
                      value={formData.meetingLink}
                      onChange={handleChange}
                      error={!!errors.meetingLink}
                      helperText={errors.meetingLink}
                      disabled={isSubmitting}
                      inputProps={{ 'aria-required': 'true' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="accessCode"
                      name="accessCode"
                      label="Access Code (optional)"
                      placeholder="e.g., 123456"
                      value={formData.accessCode}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                </>
              )}

              {/* Phone Call fields */}
              {formData.meetingType === 'PHONE' && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      id="phoneNumber"
                      name="phoneNumber"
                      label="Meeting Dial-in Number"
                      type="tel"
                      placeholder="e.g., (555) 123-4567"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber}
                      disabled={isSubmitting}
                      inputProps={{ 'aria-required': 'true' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      id="accessCode"
                      name="accessCode"
                      label="Access Code (optional)"
                      placeholder="e.g., 123456#"
                      value={formData.accessCode}
                      onChange={handleChange}
                      disabled={isSubmitting}
                    />
                  </Grid>
                </>
              )}

              {/* Bills to Discuss */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="billsToDiscuss-label">Bill(s) to Be Discussed</InputLabel>
                  <Select
                    labelId="billsToDiscuss-label"
                    id="billsToDiscuss"
                    name="billsToDiscuss"
                    multiple
                    value={formData.billsToDiscuss ? formData.billsToDiscuss.split(', ') : []}
                    label="Bill(s) to Be Discussed"
                    onChange={(e) => {
                      const value = e.target.value;
                      const selected = typeof value === 'string' ? value : value.join(', ');
                      setFormData((prev) => ({ ...prev, billsToDiscuss: selected }));
                      if (formState === 'IDLE') setFormState('FILLING');
                    }}
                    disabled={isSubmitting}
                    sx={{ '& .MuiSelect-select': { whiteSpace: 'normal', overflowWrap: 'anywhere' } }}
                    renderValue={(selected) => (selected as string[]).join(', ')}
                  >
                    {billOptions.map((bill) => (
                      <MenuItem key={bill} value={bill}>
                        {bill}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {/* Additional Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  id="additionalNotes"
                  name="additionalNotes"
                  label="Additional Context or Notes (optional)"
                  placeholder="Any additional information that would help prepare for this meeting..."
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  inputProps={{ maxLength: 2000 }}
                />
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* reCAPTCHA */}
        <Grid item xs={12}>
          <Box sx={{ my: 1 }}>
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'}
              onChange={handleCaptchaChange}
              onExpired={handleCaptchaExpired}
              size="compact"
              theme={isDarkMode ? 'dark' : 'light'}
            />
            {errors.recaptcha && (
              <FormHelperText error role="alert" sx={{ mt: 1 }}>
                {errors.recaptcha}
              </FormHelperText>
            )}
          </Box>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isSubmitting}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minHeight: 48,
              py: 1.5,
              fontSize: '1.1rem',
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                Submitting...
              </>
            ) : (
              'Send Meeting Request'
            )}
          </Button>
        </Grid>
      </Grid>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleCloseSuccessDialog}
        maxWidth="sm"
        fullWidth
        aria-labelledby="success-dialog-title"
      >
        <DialogTitle id="success-dialog-title" sx={{ pr: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
            Meeting Request Submitted
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleCloseSuccessDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Thank you for your meeting request! We've received your information and will review it shortly.
          </Typography>
          <Typography variant="body1" paragraph>
            A confirmation email has been sent to your email address with the details you submitted.
          </Typography>
          <Typography variant="body1">
            Our team will reach out within 1-2 business days to coordinate scheduling.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSuccessDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LegislatorMeetingForm;
